import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { makeStyles, useTheme } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tab from '@mui/material/Tab';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import clsx from 'clsx';
import Home from './Home';
import PcpnRegion from './PcpnRegion';
import PcpnStation from './PcpnStation';
import UsgsRegion from './UsgsRegion';
import UsgsStation from './UsgsStation';
import ResvRegion from './ResvRegion';
import ResvStation from './ResvStation';
import KbdiRegion from './KbdiRegion';
import EddiRegion from './EddiRegion';
import EddiStation from './EddiStation';
import KbdiStation from './KbdiStation';
import LinkedImage from './LinkedImage';
import AccumPcpnGrf from './AccumPcpnGrf';
import AccumPetDptGrf from './AccumPetDptGrf';
import { getDefaultWeekDate, getDefaultDate } from './routines';

const drawerWidth = 200;

const useStyles = makeStyles((theme) => ({
  appBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
    "&:hover": {
      color: "#3f51b5",
      backgroundColor: "lightblue !important",
    },
  },
  drawerPaper: {
    width: drawerWidth,
    display: "flex",
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(0),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: drawerWidth,
  }
}));

const Main = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [ value, setValue ] = useState('home');
  const [ availSize, setAvailSize ] = useState({});
  const [ searchParams ] = useSearchParams();
  const [ dcr, setDcr ] = useState(false);
	const smallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [ open, setOpen ] = useState(!smallScreen);
  const [ yr, mon ] = getDefaultDate().split("-");
  const day = new Date(parseInt(yr), parseInt(mon), 0).getDate(); // last day of month
  const weekdate = getDefaultWeekDate();

  // for NRCC maps
  const shortMonth = ["","jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const mmmyy = shortMonth[parseInt(mon)]+yr.slice(2,4);

  // display snow map choices between Nov and Apr
  const snowOn = parseInt(mon) >= 11 || parseInt(mon) <= 4;

  // available size of results area
  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width: parseInt(width) - (smallScreen ? 0 : 200) - 32,   // width minus navigation pane and margin
      height: parseInt(height) - 64 - 32                       // height minus banner and margin
    };
  };

  // refit results to window
  useEffect(() => {
    const handleResize = () => {
        setAvailSize(getWindowDimensions());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [] );  // eslint-disable-line react-hooks/exhaustive-deps

  // drawer is the navigation pane
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleValueChange = (event, value) => {
    const newValue = value ? value : event.currentTarget.value;   //tab/link clicks are value; button clicks are event.currentTarget.value
    setTimeout(() => {setValue(newValue)}, 200);                  //need timeout to have time for drawer to close before rendering results
  };

  useEffect(() => {
    // initial useState does not recognize small screen yet; so need to set drawer status and available window size here
    setOpen(!smallScreen);
    setAvailSize(getWindowDimensions());
  }, [smallScreen]);  // eslint-disable-line react-hooks/exhaustive-deps

  // check for DCR "private" menu
  useEffect(() => {
    setDcr(searchParams.has("user") && searchParams.get("user").toLowerCase() === "dcr");
    // eslint-disable-next-line
  }, [searchParams]);

  return <>
    <AppBar 
      role="banner"
      position="fixed"
      className={clsx(classes.appBar, { [classes.appBarShift]: open, })}
    >
      <a className="skip" href="#maincontent">Skip navigation</a>
      <Toolbar>
        {!open &&
          <Tooltip title="Open menu">
            <IconButton
              color="inherit"
              aria-label="open menu drawer"
              onClick={handleDrawerOpen}
              edge="start"
              className={classes.menuButton}
              size="large">
              <MenuIcon />
            </IconButton>
          </Tooltip>
        }
      </Toolbar>
    </AppBar>

    <TabContext value={value}>
      <nav>
        <Drawer
          variant="persistent"
          anchor="left"
          open={open}
          sx={{width: drawerWidth, flexShrink: 0}}
          classes={{ paper: classes.drawerPaper }}
        >
          <div className={classes.drawerHeader}>
            <Tooltip title="Close menu">
              <IconButton
                className={classes.menuButton}
                aria-label="close menu"
                onClick={handleDrawerClose}
                size="large">
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Divider />
          <TabList
            onChange={handleValueChange} 
            onClick={()=>{if(smallScreen){handleDrawerClose()}}} 
            orientation="vertical" 
            variant="scrollable" 
            aria-label="tab navigation"
          >
            <Tab variant="styledTab" tabIndex={0} label="Home" value="home" />
            <Tab variant="dividerTab" tabIndex={0} disabled label="---- Monthly updates ----" value="" />
            <Tab variant="styledTab" tabIndex={0} label="Precipitation" value="pcpnregion" />
            <Tab variant="styledTab" tabIndex={0} label="Streamflow" value="sfregion" />
            <Tab variant="styledTab" tabIndex={0} label="Groundwater" value="gwregion" />
            <Tab variant="styledTab" tabIndex={0} label="Lakes/Impoundments" value="liregion" />
            <Tab variant="styledTab" tabIndex={0} label="KBDI" value="kbdiregion" />
            <Tab variant="styledTab" tabIndex={0} label="Evapotranspiration" value="eddiregion" />
            <Tab variant="dividerTab" tabIndex={0} disabled label="---- Weekly updates ----" value="" />
            <Tab variant="styledTab" tabIndex={0} label="Precipitation" value="pcpnwkregion" />
            <Tab variant="styledTab" tabIndex={0} label="Streamflow" value="sfwkregion" />
            <Tab variant="styledTab" tabIndex={0} label="Groundwater" value="gwwkregion" />
            <Tab variant="styledTab" tabIndex={0} label="KBDI" value="kbdiwkregion" />
            <Tab variant="styledTab" tabIndex={0} label="Evapotranspiration" value="eddiwkregion" />
            <Tab variant="dividerTab" tabIndex={0} disabled label="---- Daily updates ----" value="" />
            <Tab variant="styledTab" tabIndex={0} label="Accum Precip Graph" value="pcpngrf" />
            <Tab variant="styledTab" tabIndex={0} label="Accum PET Graph" value="petgrf" />
            <Tab variant="styledTab" tabIndex={0} label="KBDI" value="kbdidlyregion" />
            <Tab variant={dcr?"dividerTab":"noshow"} tabIndex={0} disabled label="---- Basin Monthly ----" value="" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Precipitation" value="pcpnbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Streamflow" value="sfbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Groundwater" value="gwbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="KBDI" value="kbdibasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Evapotranspiration" value="eddibasin" />
            <Tab variant={dcr?"dividerTab":"noshow"} tabIndex={0} disabled label="---- Basin Weekly ----" value="" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Precipitation" value="pcpnwkbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Streamflow" value="sfwkbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Groundwater" value="gwwkbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="KBDI" value="kbdiwkbasin" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Evapotranspiration" value="eddiwkbasin" />

            <Tab variant={dcr?"dividerTab":"noshow"} tabIndex={0} disabled label="---- County Monthly ----" value="" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Precipitation" value="pcpncounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Streamflow" value="sfcounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Groundwater" value="gwcounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="KBDI" value="kbdicounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Evapotranspiration" value="eddicounty" />
            <Tab variant={dcr?"dividerTab":"noshow"} tabIndex={0} disabled label="---- County Weekly ----" value="" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Precipitation" value="pcpnwkcounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Streamflow" value="sfwkcounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Groundwater" value="gwwkcounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="KBDI" value="kbdiwkcounty" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Evapotranspiration" value="eddiwkcounty" />

            <Tab variant="dividerTab" tabIndex={0} disabled label="---- Outlooks ----" value="" />
            <Tab variant="styledTab" tabIndex={0} label="7-Day Precipitation" value="qpf7dy" />
            <Tab variant="styledTab" tabIndex={0} label="Monthly/Seasonal" value="msoutlook" />
            <Tab variant="styledTab" tabIndex={0} label="Monthly Drought" value="mdoutlook" />
            <Tab variant="styledTab" tabIndex={0} label="Seasonal Drought" value="sdoutlook" />
            <Tab variant="dividerTab" tabIndex={0} disabled label="---- Other ----" value="" />
            <Tab variant={dcr?"styledTab":"noshow"} tabIndex={0} label="Latest USDM Map" value="usdm" />
            <Tab variant="styledTab" tabIndex={0} label="Temp Departure Map" value="tdpt" />
            <Tab variant="styledTab" tabIndex={0} label="Streamflow Pct Graph" value="sfplot" />
            <Tab variant="styledTab" sx={snowOn?{}:{display:"none"}} tabIndex={0} label="Snow Departure Map" value="sdpt" />
            <Tab variant="styledTab" sx={snowOn?{}:{display:"none"}} tabIndex={0} label="Snow Water Equiv Map" value="swe" />

            <Tab variant="noshow" disabled label="----- Monthly Station Tables -----" value="" />
            <Tab variant="noshow" label="Precipitation" value="pcpnstation" />
            <Tab variant="noshow" label="Lakes/Impoundments" value="listation" />
            <Tab variant="noshow" label="Streamflow" value="sfstation" />
            <Tab variant="noshow" label="Groundwater" value="gwstation" />
            <Tab variant="noshow" label="KBDI" value="kbdistation" />
            <Tab variant="noshow" label="Evapotranspiration" value="eddistation" />
            <Tab variant="noshow" disabled label="----- Weekly Station Tables -----" value="" />
            <Tab variant="noshow" label="Precipitation" value="pcpnwkstation" />
            <Tab variant="noshow" label="Streamflow" value="sfwkstation" />
            <Tab variant="noshow" label="Groundwater" value="gwwkstation" />
            <Tab variant="noshow" label="KBDI" value="kbdiwkstation" />
            <Tab variant="noshow" label="Evapotranspiration" value="eddiwkstation" />
            <Tab variant="noshow" disabled label="----- Daily Station Tables -----" value="" />
            <Tab variant="noshow" label="KBDI" value="kbdidlystation" />
            <Tab variant="noshow" disabled label="----- Basin Monthly Station Tables -----" value="" />
            <Tab variant="noshow" label="Precipitation" value="pcpnbasinstation" />
            <Tab variant="noshow" label="Streamflow" value="sfbasinstation" />
            <Tab variant="noshow" label="Groundwater" value="gwbasinstation" />
            <Tab variant="noshow" label="KBDI" value="kbdibasinstation" />
            <Tab variant="noshow" label="Evapotranspiration" value="eddibasinstation" />
            <Tab variant="noshow" disabled label="----- Basin Weekly Station Tables -----" value="" />
            <Tab variant="noshow" label="Precipitation" value="pcpnwkbasinstation" />
            <Tab variant="noshow" label="Streamflow" value="sfwkbasinstation" />
            <Tab variant="noshow" label="Groundwater" value="gwwkbasinstation" />
            <Tab variant="noshow" label="KBDI" value="kbdiwkbasinstation" />
            <Tab variant="noshow" label="Evapotranspiration" value="eddiwkbasinstation" />
            <Tab variant="noshow" disabled label="----- County Monthly Station Tables -----" value="" />
            <Tab variant="noshow" label="Precipitation" value="pcpncountystation" />
            <Tab variant="noshow" label="Streamflow" value="sfcountystation" />
            <Tab variant="noshow" label="Groundwater" value="gwcountystation" />
            <Tab variant="noshow" label="KBDI" value="kbdicountystation" />
            <Tab variant="noshow" label="Evapotranspiration" value="eddicountystation" />
            <Tab variant="noshow" disabled label="---- County Weekly Station Tables -----" value="" />
            <Tab variant="noshow" label="Precipitation" value="pcpnwkcountystation" />
            <Tab variant="noshow" label="Streamflow" value="sfwkcountystation" />
            <Tab variant="noshow" label="Groundwater" value="gwwkcountystation" />
            <Tab variant="noshow" label="KBDI" value="kbdiwkcountystation" />
            <Tab variant="noshow" label="Evapotranspiration" value="eddiwkcountystation" />
           </TabList>
        </Drawer>
      </nav>

      <main 
        id="maincontent" 
        tabIndex={-1}
        className={clsx(classes.content, { [classes.contentShift]: open, })}>
        <div className={classes.drawerHeader} />
        <TabPanel sx={{backgroundColor:"azure"}} value="home">
          <Home/>
        </TabPanel>

        <TabPanel value="pcpnregion">
          <PcpnRegion
            handleButtonClick={handleValueChange}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="sfregion">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            sfgw="sf"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="gwregion">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            sfgw="gw"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="liregion">
          <ResvRegion
            handleButtonClick={handleValueChange}
          />
        </TabPanel>
        <TabPanel value="kbdiregion">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="eddiregion">
          <EddiRegion
            handleButtonClick={handleValueChange}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="pcpnbasin">
          <PcpnRegion
            handleButtonClick={handleValueChange}
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="sfbasin">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            sfgw="sf"
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="gwbasin">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            sfgw="gw"
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="kbdibasin">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="eddibasin">
          <EddiRegion
            handleButtonClick={handleValueChange}
            areaType="basin"
          />
        </TabPanel>

        <TabPanel value="pcpnwkregion">
          <PcpnRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="sfwkregion">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="sf"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="gwwkregion">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="gw"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="kbdiwkregion">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="eddiwkregion">
          <EddiRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="pcpnwkbasin">
          <PcpnRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="sfwkbasin">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="sf"
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="gwwkbasin">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="gw"
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="kbdiwkbasin">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="eddiwkbasin">
          <EddiRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="basin"
          />
        </TabPanel>


        <TabPanel value="pcpncounty">
          <PcpnRegion
            handleButtonClick={handleValueChange}
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="sfcounty">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            sfgw="sf"
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="gwcounty">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            sfgw="gw"
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="kbdicounty">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="eddicounty">
          <EddiRegion
            handleButtonClick={handleValueChange}
            areaType="county"
          />
        </TabPanel>
        
        <TabPanel value="pcpnwkcounty">
          <PcpnRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="sfwkcounty">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="sf"
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="gwwkcounty">
          <UsgsRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="gw"
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="kbdiwkcounty">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="eddiwkcounty">
          <EddiRegion
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="county"
          />
        </TabPanel>


        <TabPanel value="kbdidlyregion">
          <KbdiRegion
            handleButtonClick={handleValueChange}
            date="daily"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="pcpngrf">
          <AccumPcpnGrf
            smallScreen={smallScreen}
          />
        </TabPanel>
        <TabPanel value="petgrf">
          <AccumPetDptGrf
            smallScreen={smallScreen}
          />
        </TabPanel>

        <TabPanel value="pcpnstation">
          <PcpnStation
            handleButtonClick={handleValueChange}
            areaType = "region"
          />
        </TabPanel>
       <TabPanel value="listation">
          <ResvStation
            handleButtonClick={handleValueChange}
          />
        </TabPanel>
        <TabPanel value="sfstation">
          <UsgsStation 
            handleButtonClick={handleValueChange}
            sfgw="sf"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="gwstation">
          <UsgsStation 
            handleButtonClick={handleValueChange}
            sfgw="gw"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="kbdistation">
          <KbdiStation 
            handleButtonClick={handleValueChange}
            areaType = "region"
          />
        </TabPanel>
        <TabPanel value="eddistation">
          <EddiStation 
            handleButtonClick={handleValueChange}
            areaType = "region"
          />
        </TabPanel>
        <TabPanel value="pcpnbasinstation">
          <PcpnStation
            handleButtonClick={handleValueChange}
            areaType = "basin"
          />
        </TabPanel>
        <TabPanel value="sfbasinstation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            sfgw="sf"
            areaType = "basin"
          />
        </TabPanel>
        <TabPanel value="gwbasinstation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            sfgw="gw"
            areaType = "basin"
          />
        </TabPanel>
        <TabPanel value="kbdibasinstation">
          <KbdiStation
            handleButtonClick={handleValueChange}
            areaType = "basin"
          />
        </TabPanel>
        <TabPanel value="eddibasinstation">
          <EddiStation
            handleButtonClick={handleValueChange}
            areaType = "basin"
          />
        </TabPanel>
        <TabPanel value="pcpncountystation">
          <PcpnStation
            handleButtonClick={handleValueChange}
            areaType = "county"
          />
        </TabPanel>
        <TabPanel value="sfcountystation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            sfgw="sf"
            areaType = "county"
          />
        </TabPanel>
        <TabPanel value="gwcountystation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            sfgw="gw"
            areaType = "county"
          />
        </TabPanel>
        <TabPanel value="kbdicountystation">
          <KbdiStation
            handleButtonClick={handleValueChange}
            areaType = "county"
          />
        </TabPanel>
        <TabPanel value="eddicountystation">
          <EddiStation
            handleButtonClick={handleValueChange}
            areaType = "county"
          />
        </TabPanel>

        <TabPanel value="pcpnwkstation">
          <PcpnStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="sfwkstation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="sf"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="gwwkstation">
          <UsgsStation 
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="gw"
            areaType="region"
          />
        </TabPanel>
        <TabPanel value="kbdiwkstation">
          <KbdiStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType = "region"
          />
        </TabPanel>
        <TabPanel value="eddiwkstation">
          <EddiStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType = "region"
          />
        </TabPanel>

        <TabPanel value="pcpnwkbasinstation">
          <PcpnStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="sfwkbasinstation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="sf"
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="gwwkbasinstation">
          <UsgsStation 
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="gw"
            areaType="basin"
          />
        </TabPanel>
        <TabPanel value="kbdiwkbasinstation">
          <KbdiStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType = "basin"
          />
        </TabPanel>
       <TabPanel value="eddiwkbasinstation">
          <EddiStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType = "basin"
          />
        </TabPanel>
        <TabPanel value="pcpnwkcountystation">
          <PcpnStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="sfwkcountystation">
          <UsgsStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="sf"
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="gwwkcountystation">
          <UsgsStation 
            handleButtonClick={handleValueChange}
            date={weekdate}
            sfgw="gw"
            areaType="county"
          />
        </TabPanel>
        <TabPanel value="kbdiwkcountystation">
          <KbdiStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType = "county"
          />
        </TabPanel>
        <TabPanel value="eddiwkcountystation">
          <EddiStation
            handleButtonClick={handleValueChange}
            date={weekdate}
            areaType = "county"
          />
        </TabPanel>


        <TabPanel value="kbdidlystation">
          <KbdiStation
            handleButtonClick={handleValueChange}
            date="daily"
            areaType="region"
          />
        </TabPanel>

        <TabPanel value="msoutlook">
          <LinkedImage
            link="https://www.cpc.ncep.noaa.gov/products/predictions/multi_season/13_seasonal_outlooks/color/page2.gif"
            alt="Monthly and seasonal outlook maps"
          />
        </TabPanel>
        <TabPanel value="mdoutlook">
          <LinkedImage
            link="https://www.cpc.ncep.noaa.gov/products/expert_assessment/month_drought.png"
            alt="Monthly drought outlook map"
          />
        </TabPanel>
        <TabPanel value="sdoutlook">
          <LinkedImage
            link="https://www.cpc.ncep.noaa.gov/products/expert_assessment/season_drought.png"
            alt="Seasonal drought outlook map"
          />
        </TabPanel>
        <TabPanel value="qpf7dy">
          <LinkedImage
            link="https://www.wpc.ncep.noaa.gov/qpf/p168i.gif"
            alt="7-day QPF map"
          />
        </TabPanel>
        <TabPanel value="usdm">
          <LinkedImage
            link="https://droughtmonitor.unl.edu/data/png/current/current_MA_text.png"
            alt="Latest USDM"
            availSize={availSize}
          />
        </TabPanel>
        <TabPanel value="tdpt">
          <LinkedImage
            link={"https://www.nrcc.cornell.edu/dyn_images/ma_maps/mtdpt_ma_"+mmmyy+".png"}
            alt="Monthly temperature departure map"
            source="Northeast Regional Climate Center"
            availSize={availSize}
          />
        </TabPanel>
        <TabPanel value="sfplot">
          <LinkedImage
            link="https://waterwatch.usgs.gov/regplots/real/real_ma_sum_1.gif"
            alt="Streamflow percent"
            link2={`${process.env.PUBLIC_URL}/images/SfTimeSeriesLegend.png`}
            alt2="Streamflow percent legend"
            availSize={availSize}
          />
        </TabPanel>
        <TabPanel value="sdpt">
          <LinkedImage
            link={"https://www.nrcc.cornell.edu/dyn_images/ma_maps/msdpt_ma_"+mmmyy+".png"}
            alt="Monthly snowfall departure map"
            availSize={availSize}
            source="Northeast Regional Climate Center"
          />
        </TabPanel>
        <TabPanel value="swe">
          <LinkedImage
            link={"https://www.nohrsc.noaa.gov/snow_model/images/full/Northeast/nsm_swe/"+yr+mon+"/nsm_swe_"+yr+mon+day+"05_Northeast.jpg"}
            alt="Snow water equivalent map"
          />
        </TabPanel>
      </main>
    </TabContext>
  </>;
}

export default Main;
